import React, { useCallback, useState } from "react";
import { useHistory } from "react-router-dom";
// eslint-disable-next-line import/no-deprecated -- this line was auto generated, hence fix the issue timely
import { useActions } from "../../../store";
import { actions as OrderActions } from "../../../store/order/actions";
import { actions as LoaderActions } from "../../../store/loadingScreen/actions";
import { clientFetch } from "@flights/client-fetch";
import { UIOrder, UIOrderExtras, UIPriceBreakdown } from "@flights/types";
import { usePaymentComponent } from "../../../hooks/usePaymentComponent";
import { assert } from "../../../utils/assert";
import { useTestConfirmation } from "../ConfirmationController";

import useCreateUrl from "../../../hooks/useCreateUrl";
import useContextName from "../../../hooks/useContextName";
import useGlobalContext from "../../../hooks/useGlobalContext";
import useEvents from "../../../hooks/useEvents";
import useUserAgent from "../../../hooks/useUserAgent";
import { useSelector } from "react-redux";
import { getAddOnOrder, getOrder } from "../../../store/order/selectors";
import { pricingUtils } from "@flights/web-api-utils-universal";

export type PostBookingPaymentControllerData = ReturnType<typeof usePostBookingPayment>;

interface Props {
  children: (data: PostBookingPaymentControllerData) => React.ReactElement;
  postBookingModalPaymentCallback?: () => void;
}

const usePostBookingPayment = function (postBookingModalPaymentCallback?: () => void) {
  const history = useHistory();
  const { createUrl } = useCreateUrl();
  const order = useSelector(getOrder);
  const addOnOrder = useSelector(getAddOnOrder);
  const { jsError, grumble } = useEvents();
  // eslint-disable-next-line import/no-deprecated -- this line was auto generated, hence fix the issue timely
  const loaderActions = useActions(LoaderActions);
  // eslint-disable-next-line import/no-deprecated -- this line was auto generated, hence fix the issue timely
  const orderActions = useActions(OrderActions);
  const { isMobile } = useUserAgent();
  const [isFinalizeLoading, setFinalizeLoading] = useState(false);

  useTestConfirmation();

  const contextName = useContextName();
  const { requestId } = useGlobalContext();

  if (!order) {
    throw new Error("Missing order data");
  }
  if (!addOnOrder) {
    throw new Error("Missing addOnOrder data");
  }

  const getPurchasedAncillary = useCallback(
    (addOnOrderId: string) => {
      if (!addOnOrder || !addOnOrderId) return null;
      const ancillary =
        order &&
        order.addOnOrders?.find((addonOrder) => {
          return addonOrder.addOnOrderId === addOnOrderId;
        })?.ancillaries;

      return ancillary ? Object.keys(ancillary)[0] : null;
    },
    [addOnOrder, order]
  );

  const goToConfirmation = useCallback(() => {
    if (getPurchasedAncillary(addOnOrder.addOnOrderId) === "fastTrack") {
      isMobile
        ? history.replace(
            createUrl(`/booking/add-fast-track/${order.orderToken}/confirmation/?aoid=${addOnOrder.addOnOrderId}`)
          )
        : postBookingModalPaymentCallback?.();
    } else {
      history.replace(createUrl(`/booking/checkout/${order.orderToken}/confirmation/?aoid=${addOnOrder.addOnOrderId}`));
    }
  }, [
    postBookingModalPaymentCallback,
    history,
    createUrl,
    order.orderToken,
    addOnOrder.addOnOrderId,
    getPurchasedAncillary,
    isMobile
  ]);

  const finalize = useCallback(() => {
    loaderActions.show();
    setFinalizeLoading(true);
    grumble("successful or pending post booking payment call", { requestId, orderId: order.orderId });
    return clientFetch<UIOrder>(`/api/order/${order.orderToken}/addon/finalize`, {
      body: JSON.stringify({
        addOnOrderId: addOnOrder.addOnOrderId
      }),
      method: "post",
      credentials: "same-origin",
      headers: {
        "Content-Type": "application/json",
        "X-Flights-Context-Name": contextName,
        "X-Booking-Experiment-Generate-New-Clientside-Payload": "1"
      }
    })
      .then((data) => {
        orderActions.fetchSuccess(data);
      })
      .catch((error) => {
        //no matter where the error was, we shall catch and report it
        jsError(error);
      })
      .finally(() => {
        loaderActions.hide();
        goToConfirmation();
        setFinalizeLoading(false);
      });
  }, [
    loaderActions,
    order.orderToken,
    addOnOrder.addOnOrderId,
    orderActions,
    goToConfirmation,
    contextName,
    grumble,
    requestId,
    order.orderId,
    jsError
  ]);

  const failedCallback = useCallback(() => {
    grumble("Failed post booking payment call", { requestId, orderId: order.orderId });
    return Promise.resolve();
  }, [grumble, requestId, order.orderId]);

  const paymentId = addOnOrder.paymentId;
  assert(paymentId);
  const paymentComponentProductType = order?.supplierInfo?.paymentComponentProductType;

  const { pcProcessPayment, pcStatus, pcIsLoaded, pcContainerRef } = usePaymentComponent({
    paymentId,
    paymentComponentProductType,
    successfulCallback: finalize,
    pendingCallback: finalize,
    failedCallback,
    orderId: order.orderId,
    type: "AddOnOrder"
  });

  const onPayClick = useCallback(() => {
    pcProcessPayment();
  }, [pcProcessPayment]);

  const getTotalPrice = useCallback((ancillaries: UIOrderExtras): UIPriceBreakdown => {
    return pricingUtils.sumPriceBreakdown(
      Object.keys(ancillaries).map((productType) => {
        // @ts-expect-error: Element implicitly has an 'any' type. Fix the issue timely.
        return ancillaries[productType].price;
      })
    );
  }, []);

  return {
    onPayClick,
    pcContainerRef,
    pcStatus,
    pcIsLoaded,
    getTotalPrice,
    isFinalizeLoading
  };
};

const PostBookingPaymentController: React.FunctionComponent<Props> = (props) => {
  const postBookingPaymentData = usePostBookingPayment(props.postBookingModalPaymentCallback);
  return props.children(postBookingPaymentData);
};

export default PostBookingPaymentController;
